import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Grid, Typography } from '@material-ui/core';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useTheme } from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import ScrollRevealSection from './ScrollRevealSection';

/**
 * Shared interactive setup tour — used on homepage and /platform.
 */
export default function InteractiveSetupWalkthrough({
    steps,
    sectionId,
    ariaHeadingId,
    idPrefix,
    heading,
    subtitle,
    maxW = '1240px',
    showBottomBorder = true,
}) {
    const theme = useTheme();
    const md = useMediaQuery(theme.breakpoints.up('md'));
    const lg = useMediaQuery(theme.breakpoints.up('lg'));
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const [active, setActive] = useState(0);
    const tablistRef = useRef(null);
    const duration = reduceMotion ? 0 : 0.52;

    const go = useCallback(
        (index) => {
            if (index < 0 || index >= steps.length) return;
            setActive(index);
        },
        [steps],
    );

    useEffect(() => {
        const onKey = (e) => {
            if (!tablistRef.current || !tablistRef.current.contains(document.activeElement)) return;
            if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                go(Math.min(active + 1, steps.length - 1));
            } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                go(Math.max(active - 1, 0));
            } else if (e.key === 'Home') {
                e.preventDefault();
                go(0);
            } else if (e.key === 'End') {
                e.preventDefault();
                go(steps.length - 1);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [active, go, steps.length]);

    const previewHeight = lg ? 560 : md ? 480 : 340;

    const slideMotion = (isActive) =>
        reduceMotion
            ? { opacity: isActive ? 1 : 0 }
            : {
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? 'translateX(0)' : 'translateX(14px)',
              };

    const renderDesktopStep = (s, i, isActive) => (
        <Box
            key={s.id}
            role="tab"
            tabIndex={isActive ? 0 : -1}
            aria-selected={isActive}
            id={`${idPrefix}-tab-${s.id}`}
            aria-controls={`${idPrefix}-panel-${s.id}`}
            className={`home-walkthrough-step-btn home-walkthrough-step-rail${isActive ? ' home-walkthrough-step-btn--active' : ''}`}
            onClick={() => go(i)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    go(i);
                }
            }}
            style={{
                textAlign: 'left',
                padding: '14px 16px',
                borderRadius: 14,
                border: isActive
                    ? '1px solid rgba(56, 189, 248, 0.65)'
                    : '1px solid rgba(71, 85, 105, 0.72)',
                background: isActive
                    ? 'linear-gradient(165deg, rgba(56, 189, 248, 0.22) 0%, rgba(15, 23, 42, 0.94) 100%)'
                    : 'rgba(15, 23, 42, 0.48)',
                cursor: 'pointer',
                outline: 'none',
                boxShadow: isActive
                    ? '0 0 0 1px rgba(56, 189, 248, 0.12), 0 12px 40px -18px rgba(56, 189, 248, 0.35)'
                    : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
            }}
        >
            <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0, flex: 1 }}>
                <Box
                    className="home-walkthrough-step-index"
                    aria-hidden
                    style={{
                        width: 30,
                        height: 30,
                        borderRadius: 10,
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 13,
                        fontWeight: 800,
                        color: isActive ? '#042f2e' : '#94a3b8',
                        background: isActive
                            ? 'linear-gradient(145deg, #a5f3fc 0%, #22d3ee 48%, #38bdf8 100%)'
                            : 'rgba(51, 65, 85, 0.65)',
                        boxShadow: isActive ? '0 2px 12px rgba(56, 189, 248, 0.45)' : 'none',
                    }}
                >
                    {i + 1}
                </Box>
                <Box style={{ minWidth: 0 }}>
                    <Typography
                        component="span"
                        style={{
                            display: 'block',
                            fontSize: 16,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            color: '#f8fafc',
                            lineHeight: 1.3,
                        }}
                    >
                        {s.title}
                    </Typography>
                    <Typography
                        component="span"
                        style={{
                            fontSize: 12,
                            color: '#64748b',
                            fontWeight: 600,
                            letterSpacing: '0.03em',
                            marginTop: 4,
                            display: 'block',
                        }}
                    >
                        {s.tabLabel}
                    </Typography>
                </Box>
            </Box>
            <Box
                style={{
                    width: 24,
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
                aria-hidden
            >
                <ChevronRight
                    className="home-walkthrough-step-chevron"
                    style={{
                        fontSize: 22,
                        color: '#7dd3fc',
                        opacity: isActive ? 0.95 : 0,
                        visibility: isActive ? 'visible' : 'hidden',
                    }}
                />
            </Box>
        </Box>
    );

    return (
        <ScrollRevealSection
            id={sectionId}
            aria-labelledby={ariaHeadingId}
            className="home-surface-dark"
            revealVariant="fade-up-sm"
            stagger={false}
            style={{
                padding: lg ? '80px 0 88px' : md ? '60px 0 72px' : '48px 0 56px',
                background: 'linear-gradient(180deg, #0f172a 0%, #0c1426 45%, #0b1220 100%)',
                scrollMarginTop: '72px',
                ...(showBottomBorder ? { borderBottom: '1px solid rgba(51, 65, 85, 0.45)' } : {}),
            }}
        >
            <Box maxWidth={maxW} margin="0 auto" style={{ padding: md ? '0 24px' : '0 18px' }}>
                <Typography
                    component="h2"
                    id={ariaHeadingId}
                    className="home-section-h2"
                    style={{
                        fontSize: lg ? '38px' : md ? '30px' : '22px',
                        marginBottom: md ? '12px' : '8px',
                        textAlign: 'center',
                        fontWeight: 800,
                        letterSpacing: '-0.03em',
                    }}
                >
                    {heading}
                </Typography>
                <Typography
                    component="p"
                    style={{
                        fontSize: lg ? '17px' : md ? '16px' : '15px',
                        color: '#94a3b8',
                        textAlign: 'center',
                        maxWidth: '620px',
                        margin: '0 auto 32px',
                        lineHeight: 1.58,
                        fontWeight: 500,
                    }}
                >
                    {subtitle}
                </Typography>

                {!md ? (
                    <Box
                        ref={tablistRef}
                        role="tablist"
                        aria-label="Setup steps"
                        style={{
                            display: 'flex',
                            gap: 8,
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            paddingBottom: 12,
                            marginBottom: 8,
                            scrollbarWidth: 'thin',
                        }}
                    >
                        {steps.map((s, i) => {
                            const isActive = i === active;
                            return (
                                <Box
                                    key={s.id}
                                    role="tab"
                                    tabIndex={isActive ? 0 : -1}
                                    aria-selected={isActive}
                                    id={`${idPrefix}-tab-${s.id}`}
                                    aria-controls={`${idPrefix}-panel-${s.id}`}
                                    className={`home-walkthrough-step-btn${isActive ? ' home-walkthrough-step-btn--active' : ''}`}
                                    onClick={() => go(i)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            go(i);
                                        }
                                    }}
                                    style={{
                                        flex: '0 0 auto',
                                        padding: '10px 14px',
                                        borderRadius: 12,
                                        border: isActive
                                            ? '1px solid rgba(56, 189, 248, 0.65)'
                                            : '1px solid rgba(71, 85, 105, 0.72)',
                                        background: isActive
                                            ? 'linear-gradient(165deg, rgba(56, 189, 248, 0.22) 0%, rgba(15, 23, 42, 0.9) 100%)'
                                            : 'rgba(15, 23, 42, 0.48)',
                                        cursor: 'pointer',
                                        outline: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 6,
                                        boxShadow: isActive
                                            ? '0 0 0 1px rgba(56, 189, 248, 0.12), 0 8px 28px -12px rgba(56, 189, 248, 0.3)'
                                            : 'none',
                                    }}
                                >
                                    <Typography
                                        component="span"
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 800,
                                            color: isActive ? '#e0f2fe' : '#94a3b8',
                                            letterSpacing: '0.04em',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {i + 1}. {s.tabLabel}
                                    </Typography>
                                    <Box style={{ width: 18, display: 'flex', justifyContent: 'center' }} aria-hidden>
                                        <ChevronRight
                                            style={{
                                                fontSize: 18,
                                                color: '#7dd3fc',
                                                opacity: isActive ? 0.9 : 0,
                                                visibility: isActive ? 'visible' : 'hidden',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                ) : null}

                <Grid container spacing={md ? 4 : 3} alignItems="flex-start">
                    {md ? (
                        <Grid item xs={12} md={3}>
                            <Box
                                ref={tablistRef}
                                role="tablist"
                                aria-label="Setup steps"
                                className="home-walkthrough-timeline-rail"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 10,
                                    borderLeft: '2px solid rgba(71, 85, 105, 0.48)',
                                    paddingLeft: 18,
                                    marginLeft: 8,
                                    paddingTop: 6,
                                    paddingBottom: 6,
                                }}
                            >
                                {steps.map((s, i) => renderDesktopStep(s, i, i === active))}
                            </Box>
                        </Grid>
                    ) : null}

                    <Grid item xs={12} md={9}>
                        <Box
                            className="home-walkthrough-preview-column"
                            role="tabpanel"
                            id={`${idPrefix}-panel-${steps[active].id}`}
                            aria-labelledby={`${idPrefix}-tab-${steps[active].id}`}
                        >
                            {!md ? (
                                <Box
                                    key={`mob-head-${steps[active].id}`}
                                    className="home-walkthrough-copy-fade"
                                    style={{ marginBottom: 14 }}
                                >
                                    <Typography
                                        component="h3"
                                        style={{
                                            fontSize: 18,
                                            fontWeight: 800,
                                            letterSpacing: '-0.02em',
                                            color: '#f8fafc',
                                            marginBottom: 8,
                                        }}
                                    >
                                        {steps[active].title}
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontSize: 15,
                                            color: '#cbd5e1',
                                            lineHeight: 1.55,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {steps[active].benefit}
                                    </Typography>
                                </Box>
                            ) : null}

                            <Box className="home-walkthrough-preview-glass" style={{ marginBottom: 0 }}>
                                <Box
                                    className="home-walkthrough-preview-inner"
                                    style={{
                                        position: 'relative',
                                        borderRadius: md ? 20 : 16,
                                        overflow: 'hidden',
                                        height: previewHeight,
                                        background:
                                            'linear-gradient(165deg, rgba(15, 23, 42, 0.95) 0%, rgba(8, 12, 22, 0.98) 55%, #05080f 100%)',
                                        border: '1px solid rgba(148, 163, 184, 0.12)',
                                        boxShadow:
                                            'inset 0 1px 0 rgba(255,255,255,0.06), 0 4px 24px rgba(0,0,0,0.35), 0 0 60px -12px rgba(56, 189, 248, 0.14)',
                                    }}
                                >
                                    {steps.map((s, i) => {
                                        const isActive = i === active;
                                        return (
                                            <Box
                                                key={s.id}
                                                aria-hidden={!isActive}
                                                className="home-walkthrough-slide"
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    zIndex: 2,
                                                    pointerEvents: 'none',
                                                    ...slideMotion(isActive),
                                                    transition: reduceMotion
                                                        ? 'none'
                                                        : `opacity ${duration}s cubic-bezier(0.4, 0, 0.2, 1), transform ${duration}s cubic-bezier(0.4, 0, 0.2, 1)`,
                                                }}
                                            >
                                                <Box
                                                    style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxSizing: 'border-box',
                                                        padding: md ? '14px 18px' : '10px 12px',
                                                    }}
                                                >
                                                    <img
                                                        src={s.imageSrc}
                                                        alt={isActive ? s.imageAlt : ''}
                                                        className="home-walkthrough-preview-img"
                                                        style={{
                                                            maxWidth: '100%',
                                                            maxHeight: '100%',
                                                            width: 'auto',
                                                            height: 'auto',
                                                            objectFit: 'contain',
                                                            objectPosition: '50% 50%',
                                                            filter:
                                                                'drop-shadow(0 8px 24px rgba(0,0,0,0.35)) brightness(0.99)',
                                                            transition: reduceMotion
                                                                ? 'none'
                                                                : `opacity ${duration}s ease`,
                                                        }}
                                                    />
                                                    <Box
                                                        aria-hidden
                                                        style={{
                                                            position: 'absolute',
                                                            inset: 0,
                                                            pointerEvents: 'none',
                                                            background:
                                                                'linear-gradient(180deg, transparent 0%, transparent 55%, rgba(5,8,15,0.22) 100%)',
                                                        }}
                                                    />
                                                </Box>

                                                <Box
                                                    className="home-walkthrough-live-pill"
                                                    style={{
                                                        position: 'absolute',
                                                        bottom: md ? 16 : 12,
                                                        right: md ? 16 : 10,
                                                        zIndex: 3,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 8,
                                                        padding: '8px 14px',
                                                        borderRadius: 999,
                                                        pointerEvents: 'none',
                                                        background: 'rgba(15, 23, 42, 0.78)',
                                                        border: '1px solid rgba(56, 189, 248, 0.28)',
                                                        boxShadow:
                                                            '0 8px 32px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06)',
                                                        backdropFilter: 'blur(12px)',
                                                        WebkitBackdropFilter: 'blur(12px)',
                                                        maxWidth: md ? 300 : 'calc(100% - 20px)',
                                                    }}
                                                >
                                                    <Box
                                                        aria-hidden
                                                        style={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            background: '#22c55e',
                                                            boxShadow: '0 0 10px rgba(34, 197, 94, 0.65)',
                                                            flexShrink: 0,
                                                        }}
                                                        className="home-walkthrough-live-dot"
                                                    />
                                                    <Typography
                                                        component="span"
                                                        style={{
                                                            fontSize: md ? 12 : 11,
                                                            fontWeight: 600,
                                                            letterSpacing: '0.02em',
                                                            color: '#e2e8f0',
                                                            lineHeight: 1.35,
                                                        }}
                                                    >
                                                        {s.liveHint}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {md ? (
                                <Box
                                    key={steps[active].id}
                                    className="home-walkthrough-copy-fade"
                                    style={{ marginTop: 10 }}
                                >
                                    <Typography
                                        component="p"
                                        style={{
                                            fontSize: lg ? 17 : 16,
                                            color: '#cbd5e1',
                                            lineHeight: 1.58,
                                            fontWeight: 500,
                                            maxWidth: '680px',
                                        }}
                                    >
                                        {steps[active].benefit}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>
                    </Grid>
                </Grid>
            </Box>
        </ScrollRevealSection>
    );
}
